import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import LanguageIcon from '@mui/icons-material/LanguageOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import TagManager from 'react-gtm-module';
import useRegisterState from "../../hooks/useRegisterState";
import backEnd from "../../constants/backEnd";
import endpoints from "../../constants/endpoints";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { postResendEmail } from "../../services/postResendEmail";

const ADMIND_LOGO = "https://admindaiacademy.s3.eu-central-1.amazonaws.com/Random+Sheet/Loghi+AdMind/ADMINDLogoNegative.png";
const ADMIND_LOGO_POS = "https://admindaiacademy.s3.eu-central-1.amazonaws.com/Random+Sheet/Loghi+AdMind/AdMindLogoPos.svg";

const FEATURE_ICONS = ['✍️', '🖼️', '💼', '🤖'];

const tagManagerArgs = { gtmId: 'G-CVBJ86HRVP' };

const COOLDOWN_SECONDS = 60;

const LANGUAGES = [
    { value: 'english', label: 'English' },
    { value: 'italian', label: 'Italiano' },
    { value: 'spanish', label: 'Español' },
    { value: 'portuguese', label: 'Português' },
];

const Registration = () => {
    TagManager.initialize(tagManagerArgs);

    const {
        email, password, passwordConf, profession, marketingProfessional,
        company, submit, privacy, terms, offers, partnership,
        birthday, country, countrys, language, registered,
    } = useRegisterState();

    const navigate = useNavigate();
    const { t } = useTranslation();
    const { partnerships } = useParams();

    const [showPass, setShowPass] = React.useState(false);
    const [showPassConf, setShowPassConf] = React.useState(false);

    const [resending, setResending] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const cooldownRef = useRef(null);

    const features = t('auth.features', { returnObjects: true });

    useEffect(() => {
        countrys.set();
        if (partnerships !== undefined) {
            partnership.set(partnerships);
        }
    }, []);

    useEffect(() => {
        return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
    }, []);

    const startCooldown = () => {
        setCooldown(COOLDOWN_SECONDS);
        cooldownRef.current = setInterval(() => {
            setCooldown(prev => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleResend = async () => {
        if (cooldown > 0 || resending) return;
        const userEmail = registered.email || localStorage.getItem("userEmail");
        if (!userEmail) return;
        setResending(true);
        const response = await postResendEmail({ email: userEmail });
        setResending(false);
        if (response.type === 'success') {
            toast.success(t('registration.email_resent') || 'Email inviata di nuovo!');
            startCooldown();
        } else {
            toast.error(response.data?.detail || 'Errore nel reinvio email');
            startCooldown();
        }
    };

    const pdfHandler = (e) => {
        e.preventDefault();
        fetch(`${backEnd.url}${endpoints.access.term_pdf}`).then((response) => {
            response.blob().then((blob) => {
                const fileURL = window.URL.createObjectURL(blob);
                let alink = document.createElement("a");
                alink.href = fileURL;
                alink.download = "Terms and Conditions AdMind SRL.pdf";
                alink.click();
            });
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!terms.value) {
            toast.error(t('registration.error_terms'));
            return;
        }
        if (!privacy.value) {
            toast.error(t('registration.error_privacy'));
            return;
        }
        submit.set();
    };

    const EmailCheckScreen = () => (
        <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{
                width: 72, height: 72, borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(112,18,128,0.12), rgba(200,80,192,0.08))',
                border: '2px solid rgba(112,18,128,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 3,
            }}>
                <MarkEmailReadOutlinedIcon sx={{ fontSize: '2rem', color: '#701280' }} />
            </Box>

            <Typography variant="h2" sx={{ color: '#1C1C28', fontWeight: 700, fontSize: '1.5rem', mb: 1 }}>
                {t('registration.check_email_title') || 'Controlla la tua email'}
            </Typography>
            <Typography sx={{ color: '#6B6B7B', fontSize: '0.9rem', lineHeight: 1.7, mb: 0.5 }}>
                {t('registration.check_email_body') || 'Per favore, conferma il tuo indirizzo cliccando sul link che ti abbiamo inviato via mail.'}
            </Typography>
            {registered.email && (
                <Typography sx={{ color: '#701280', fontWeight: 600, fontSize: '0.875rem', mb: 3 }}>
                    {registered.email}
                </Typography>
            )}

            <Button
                fullWidth variant="contained"
                onClick={() => navigate('/login')}
                sx={{
                    py: 1.4, borderRadius: '12px', mb: 1.5,
                    background: 'linear-gradient(135deg, #701280, #C850C0)',
                    color: '#fff', fontWeight: 700, fontSize: '0.95rem',
                    textTransform: 'none', boxShadow: '0 4px 20px rgba(112,18,128,0.35)',
                    '&:hover': { background: 'linear-gradient(135deg, #8E1A9E, #D060D0)' },
                }}
            >
                {t('registration.go_to_login') || 'Vai al login'}
            </Button>

            <Button
                fullWidth variant="outlined"
                onClick={handleResend}
                disabled={cooldown > 0 || resending}
                sx={{
                    py: 1.3, borderRadius: '12px',
                    borderColor: cooldown > 0 ? 'rgba(0,0,0,0.12)' : '#701280',
                    color: cooldown > 0 ? '#9B9BAB' : '#701280',
                    fontWeight: 600, fontSize: '0.9rem',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#701280', background: 'rgba(112,18,128,0.04)' },
                    '&.Mui-disabled': { borderColor: 'rgba(0,0,0,0.1)', color: '#aaa' },
                }}
            >
                {resending ? (
                    <CircularProgress size={18} sx={{ color: '#701280' }} />
                ) : cooldown > 0 ? (
                    `${t('registration.resend_wait') || 'Reinvia tra'} ${cooldown}s`
                ) : (
                    t('registration.resend_email') || 'Invia una nuova mail'
                )}
            </Button>

            <Typography sx={{ color: '#9B9BAB', fontSize: '0.78rem', mt: 2, lineHeight: 1.6 }}>
                {t('registration.check_spam') || "Non hai ricevuto nulla? Controlla la cartella spam oppure attendi qualche minuto."}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex', overflow: 'hidden',
            position: 'relative', backgroundColor: '#080810',
        }}>
            {/* Left panel */}
            <Box sx={{
                display: { xs: 'none', lg: 'flex' },
                flex: 1, flexDirection: 'column',
                justifyContent: 'center', alignItems: 'flex-start',
                px: 8, py: 6, position: 'relative',
                background: '#080810', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: '-20%', left: '-15%', width: '70%', height: '70%', background: 'radial-gradient(ellipse, rgba(112,18,128,0.22) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: '-10%', right: '-10%', width: '50%', height: '50%', background: 'radial-gradient(ellipse, rgba(255,107,53,0.10) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />

                <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 480 }}>
                    <Box sx={{ mb: 5 }}>
                        <img src={ADMIND_LOGO} alt="Admind" style={{ height: 40 }} />
                    </Box>

                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 1,
                        background: 'rgba(112,18,128,0.2)', border: '1px solid rgba(112,18,128,0.4)',
                        borderRadius: '100px', px: 2, py: 0.6, mb: 3,
                    }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#00ff88' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.8)', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 600 }}>
                            {t('auth.agentic_badge')}
                        </Typography>
                    </Box>

                    <Typography variant="h1" sx={{ fontSize: '3rem', fontWeight: 900, color: '#fff', mb: 1.5, lineHeight: 1.1, letterSpacing: '-1.5px' }}>
                        {t('auth.hero_title1')}<br />
                        <Box component="span" sx={{
                            background: 'linear-gradient(135deg, #701280, #C850C0, #FF6B35)',
                            backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                        }}>
                            {t('auth.hero_title2')}
                        </Box>
                    </Typography>

                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 5, fontSize: '1rem', lineHeight: 1.7 }}>
                        {t('auth.hero_subtitle')}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, width: '100%' }}>
                        {features.map((f, idx) => (
                            <Box key={f.title} sx={{
                                display: 'flex', alignItems: 'center', gap: 2,
                                p: 1.8, borderRadius: '14px',
                                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                                backdropFilter: 'blur(10px)',
                            }}>
                                <Box sx={{
                                    width: 38, height: 38, borderRadius: '10px',
                                    background: 'linear-gradient(135deg, rgba(112,18,128,0.3), rgba(200,80,192,0.2))',
                                    border: '1px solid rgba(112,18,128,0.3)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '1.1rem', flexShrink: 0,
                                }}>
                                    {FEATURE_ICONS[idx]}
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#fff' }}>{f.title}</Typography>
                                    <Typography sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.45)' }}>{f.desc}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Right panel — white form */}
            <Box sx={{
                width: { xs: '100%', lg: '500px' },
                flexShrink: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                p: { xs: 3, sm: 4 }, position: 'relative',
                background: '#FAFAF8',
                borderLeft: { lg: '1px solid rgba(255,255,255,0.07)' },
                overflowY: 'auto',
            }}>
                {/* Language switcher top-right */}
                <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                    <LanguageSwitcher dark={false} />
                </Box>

                <Box sx={{
                    width: '100%', maxWidth: 420,
                    background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: '24px', p: { xs: 3, sm: 4 },
                    boxShadow: '0 8px 40px rgba(0,0,0,0.10)', my: 3,
                }}>
                    {registered.value ? (
                        <EmailCheckScreen />
                    ) : (
                        <>
                            <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <img src={ADMIND_LOGO_POS} alt="Admind" style={{ height: 34 }} />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h2" sx={{ color: '#1C1C28', mb: 0.5, fontWeight: 700, fontSize: '1.75rem' }}>
                                    {t('registration.title')}
                                </Typography>
                                <Typography sx={{ color: '#6B6B7B', fontSize: '0.9rem' }}>
                                    {t('registration.subtitle')}
                                </Typography>
                            </Box>

                            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <TextField
                                    fullWidth label="Email" type="email" autoComplete="email"
                                    value={email.value} onChange={(e) => email.set(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ fontSize: '1.1rem', color: '#9B9BAB' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: '12px', '&:hover fieldset': { borderColor: '#701280' }, '&.Mui-focused fieldset': { borderColor: '#701280' } },
                                        '& label.Mui-focused': { color: '#701280' },
                                    }}
                                />

                                <TextField
                                    fullWidth label="Password"
                                    type={showPass ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    value={password.value} onChange={(e) => password.set(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockIcon sx={{ fontSize: '1.1rem', color: '#9B9BAB' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setShowPass(!showPass)} edge="end">
                                                    {showPass ? <VisibilityOffIcon sx={{ fontSize: '1.1rem' }} /> : <VisibilityIcon sx={{ fontSize: '1.1rem' }} />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: '12px', '&:hover fieldset': { borderColor: '#701280' }, '&.Mui-focused fieldset': { borderColor: '#701280' } },
                                        '& label.Mui-focused': { color: '#701280' },
                                    }}
                                />

                                <TextField
                                    fullWidth label={t('registration.confirm_password')}
                                    type={showPassConf ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    value={passwordConf.value} onChange={(e) => passwordConf.set(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockIcon sx={{ fontSize: '1.1rem', color: '#9B9BAB' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setShowPassConf(!showPassConf)} edge="end">
                                                    {showPassConf ? <VisibilityOffIcon sx={{ fontSize: '1.1rem' }} /> : <VisibilityIcon sx={{ fontSize: '1.1rem' }} />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: '12px', '&:hover fieldset': { borderColor: '#701280' }, '&.Mui-focused fieldset': { borderColor: '#701280' } },
                                        '& label.Mui-focused': { color: '#701280' },
                                    }}
                                />

                                <TextField
                                    select fullWidth
                                    label={t('registration.platform_language')}
                                    value={language.value} onChange={(e) => language.set(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LanguageIcon sx={{ fontSize: '1.1rem', color: '#9B9BAB' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: '12px', '&:hover fieldset': { borderColor: '#701280' }, '&.Mui-focused fieldset': { borderColor: '#701280' } },
                                        '& label.Mui-focused': { color: '#701280' },
                                    }}
                                >
                                    {LANGUAGES.map((l) => (
                                        <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                                    ))}
                                </TextField>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={privacy.value}
                                                onChange={() => privacy.set(!privacy.value)}
                                                size="small"
                                                sx={{ color: '#9B9BAB', '&.Mui-checked': { color: '#701280' } }}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ fontSize: '0.8rem', color: '#6B6B7B' }}>
                                                {t('registration.privacy_accept')}{' '}
                                                <Link href="https://admind.ai/en/privacy-policy/" target="_blank" rel="noopener noreferrer"
                                                    sx={{ color: '#701280', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                                    Privacy Policy
                                                </Link>
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={terms.value}
                                                onChange={() => terms.set(!terms.value)}
                                                size="small"
                                                sx={{ color: '#9B9BAB', '&.Mui-checked': { color: '#701280' } }}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ fontSize: '0.8rem', color: '#6B6B7B' }}>
                                                {t('registration.terms_accept')}{' '}
                                                <Link href="#" onClick={pdfHandler}
                                                    sx={{ color: '#701280', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                                    {t('registration.terms_label')}
                                                </Link>
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={offers.value}
                                                onChange={() => offers.set(!offers.value)}
                                                size="small"
                                                sx={{ color: '#9B9BAB', '&.Mui-checked': { color: '#701280' } }}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ fontSize: '0.8rem', color: '#6B6B7B' }}>
                                                {t('registration.offers')}
                                            </Typography>
                                        }
                                    />
                                </Box>

                                <Button
                                    type="submit" fullWidth variant="contained"
                                    disabled={submit.value}
                                    sx={{
                                        mt: 0.5, py: 1.4, borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #701280, #C850C0)',
                                        color: '#fff', fontWeight: 700, fontSize: '0.95rem',
                                        textTransform: 'none', boxShadow: '0 4px 20px rgba(112,18,128,0.4)',
                                        '&:hover': { background: 'linear-gradient(135deg, #8E1A9E, #D060D0)', boxShadow: '0 6px 30px rgba(112,18,128,0.6)' },
                                        '&.Mui-disabled': { background: 'rgba(112,18,128,0.3)', color: 'rgba(255,255,255,0.6)' },
                                    }}
                                >
                                    {submit.value ? (
                                        <CircularProgress size={20} sx={{ color: '#fff' }} />
                                    ) : (
                                        t('registration.submit')
                                    )}
                                </Button>

                                <Typography sx={{ textAlign: 'center', fontSize: '0.85rem', color: '#6B6B7B', mt: 0.5 }}>
                                    {t('registration.already_account')}{' '}
                                    <Link
                                        onClick={() => navigate('/login')}
                                        sx={{ color: '#701280', cursor: 'pointer', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        {t('common.login')}
                                    </Link>
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default Registration;
